import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

// Tags marked emits for GFM, plus <span> (movement-history address markup).
const ALLOWED_TAGS = [
	'a',
	'blockquote',
	'br',
	'code',
	'del',
	'em',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'hr',
	'img',
	'input',
	'li',
	'ol',
	'p',
	'pre',
	'span',
	'strong',
	'table',
	'tbody',
	'td',
	'th',
	'thead',
	'tr',
	'ul'
];
const ALLOWED_ATTR = ['align', 'alt', 'checked', 'class', 'disabled', 'href', 'src', 'start', 'title', 'type'];
// http, https and mailto, plus scheme-less (relative) URLs such as /patients/123.
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

@Injectable({
	providedIn: 'root'
})
export class MarkdownService {
	// Own instance, so the hook below does not leak into other DOMPurify users.
	private readonly purifier = DOMPurify(window);

	constructor(private sanitizer: DomSanitizer) {
		// Note text is user-supplied (hospice users, HL7, legacy data): every link opens in a new tab.
		this.purifier.addHook('afterSanitizeAttributes', (node) => {
			if (node.tagName === 'A') {
				node.setAttribute('target', '_blank');
				node.setAttribute('rel', 'noopener noreferrer');
			}
		});

		marked.setOptions({
			breaks: true, // Convert line breaks to <br>
			gfm: true, // Enable GitHub Flavored Markdown
			pedantic: false // Don't be strict about original markdown
		});
	}

	/**
	 * Processes markdown text and returns safe HTML
	 * @param markdownText - The markdown string to process
	 * @returns SafeHtml that can be used with [innerHTML] or plain text fallback
	 */
	processMarkdown(markdownText: string): SafeHtml | string {
		// Handle empty or invalid input
		if (!markdownText || typeof markdownText !== 'string') {
			return '';
		}

		// If it's just plain text (no markdown indicators), return as is
		if (!this.hasMarkdownIndicators(markdownText)) {
			return markdownText;
		}

		try {
			// Use marked for synchronous processing
			const html = marked(markdownText) as string;
			// Sanitize first: only the purified HTML is marked trusted
			const clean = this.purifier.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOWED_URI_REGEXP });
			return this.sanitizer.bypassSecurityTrustHtml(clean);
		} catch (error) {
			console.warn('Markdown processing failed, falling back to plain text:', error);
			return markdownText; // Fallback to plain text
		}
	}

	/**
	 * Checks if the text contains markdown formatting indicators
	 * @param text - The text to check
	 * @returns true if text appears to contain markdown formatting
	 */
	private hasMarkdownIndicators(text: string): boolean {
		const markdownPatterns = [
			/^#+\s/, // Headers
			/\*\*.*\*\*/, // Bold
			/\*.*\*/, // Italic
			/^[-*+]\s/, // Unordered lists
			/^\d+\.\s/, // Ordered lists
			/\[.*\]\(.*\)/, // Links
			/`.*`/, // Inline code
			/^```/, // Code blocks
			/^>/, // Blockquotes
			/\n---\n/, // Horizontal rules
			/\|.*\|/ // Tables
		];

		return markdownPatterns.some((pattern) => pattern.test(text));
	}

	/**
	 * Check if the processed content is SafeHtml (needs [innerHTML])
	 * @param content - The content to check
	 * @returns true if content should be rendered with [innerHTML]
	 */
	isSafeHtml(content: SafeHtml | string): content is SafeHtml {
		return typeof content !== 'string';
	}
}
